<template>
	<Dialog
		:dialogVisible="dialog"
		:title="isEditing ? 'Update details' : 'Create New Contact'"
		:agreeText="buttonText"
		:cancelText="'Back'"
		:disableAgreeButton="createButtonDisabled"
		:options="$props.options"
		@cancel="cancel"
		@agree="agree">
		<template v-slot:dialog-body>
			<v-form ref="form" v-model="isFormValid">
				<v-text-field
					outlined
					v-model="customer.firstname"
					label="First name"
					:maxLength="25"
					counter="25"
					:rules="firstNameRules"></v-text-field>
				<v-text-field
					outlined
					v-model="customer.lastname"
					label="Last name"
					:maxLength="30"
					counter="30"
					validate-on="blur"
					:rules="lastNameRules"></v-text-field>
				<v-text-field
					outlined
					v-model="customer.companyName"
					:rules="companyNameRules"
					label="Company name"
					:maxLength="50"
					counter="50" />
				<v-text-field
					outlined
					v-model="emailAddress"
					:rules="emailRules"
					:label="`Email address ${emailMandatoryText ? '(Required)' : ''}`" 
					validate-on-blur/>
				<div @paste.prevent="pasteEvent">
					<vue-tel-input
						valid-color="#000000"
						v-bind="vueTelProps"
						v-model="phoneNumber"
						@input="phoneNumberChanged"
						@validate="validatePhoneNumber"
						@blur="phoneNumberBlur"
						@focus="phoneNumberFocus"
						:style="{
							height: '54px',
							marginBottom: !validPhone && showPhoneNumberFieldErrors ? '8px' : '0',
							border: showPhoneNumberFieldErrors && !validPhone ? '2px solid red !important' : '',
						}" />
				</div>
				<div v-if="showPhoneNumberFieldErrors && !validPhone" class="v-text-field__details" style="padding: 0 12px">
					<div class="v-messages theme--light error--text" role="alert">
						<div class="v-messages__wrapper">
							<div class="v-messages__message">Please enter a valid phone number.</div>
						</div>
					</div>
				</div>
			</v-form>
		</template>
	</Dialog>
</template>

<script>
	import api from "../api";
	import { addEmailToCustomer, addPhoneNumberToCustomer, calculateTimeout, getMessageFromError } from "@/helpers/utilities";
	import Dialog from "./Dialog.vue";
	import { VuetifyValidation } from "@/helpers/Validation";
	import * as Sentry from "@sentry/vue";

	export default {
		components: {
			Dialog,
		},
		props: {
			options: {
				type: Object,
				default: () => ({
					width: "500px",
				}),
			},
		},
		data() {
			return {
				dialog: false,
				resolve: null,
				reject: null,
				form: false,
				message: null,
				customer: {
					firstname: "",
					lastname: "",
					companyName: "",
					EmailAddresses: [],
					PhoneNumbers: [],
				},
				initialCustomer: null,
				emailAddress: "",
				phoneNumber: "",
				validPhone: false,
				vueTelProps: window.VueTelProps,
				creating: false,
				emailMandatoryText: null,
				isEditing: false,
				showPhoneNumberFieldErrors: false,
				isFormValid: false,
			};
		},
		async mounted() {},
		methods: {
			open(name, emailMandatoryText = null) {
				this.isEditing = false;
				this.emailMandatoryText = emailMandatoryText;
				this.dialog = true;
				this.resetForm(name);
				return new Promise((resolve, reject) => {
					this.resolve = resolve;
					this.reject = reject;
				});
			},
			async editContact(customerId, emailMandatoryText = null) {
				try {
					// First get the customer data
					const customer = await api.getCustomer(customerId);
					// Set up the dialog with customer data
					this.initialCustomer = { ...customer };
					this.customer = customer;
					this.phoneNumber = customer.PhoneNumbers[0]?.phoneNumber;
					this.emailAddress = customer.EmailAddresses[0]?.emailAddress;
					this.dialog = true;
					this.isEditing = true;
					this.emailMandatoryText = emailMandatoryText;

					// Return the promise that will resolve when dialog is completed
					return new Promise((resolve, reject) => {
						this.resolve = resolve;
						this.reject = reject;
					});
				} catch (err) {
					console.error("Error fetching customer:", err);
					throw err;
				}
			},
			resetForm(name) {
				this.$nextTick(() => {
					this.customer.EmailAddresses = [];
					this.customer.PhoneNumbers = [];
					this.phoneNumber = "";
					this.emailAddress = "";
					this.customer.firstname = "";
					this.customer.lastname = "";
					this.customer.companyName = "";
					this.showPhoneNumberFieldErrors = false;
					if (name) {
						const trimmedName = name.trim();
						if (trimmedName.includes(" ")) {
							this.customer = {
								...this.customer,
								firstname: trimmedName.split(" ")[0],
								lastname: trimmedName.split(" ").slice(1).join(" "),
							};
						} else {
							this.customer = { ...this.customer, firstname: trimmedName };
						}
					}
					this.$refs.form?.resetValidation();
				});
			},
			async agree() {
				try {
					this.showPhoneNumberFieldErrors = true;
					if (!this.$refs.form.validate() || !this.validPhone) {
						return;
					}
					this.creating = true;
					if (this.emailAddress) {
						this.customer = addEmailToCustomer(this.customer, this.emailAddress);
					}
					this.customer = addPhoneNumberToCustomer(this.customer, this.phoneNumber);
					const record = this.isEditing
						? await api.updateCustomer(this.customer.id, { ...this.customer })
						: await api.createCustomer(this.customer);
					const customer = await api.getCustomer(record.id);
					this.resolve(customer);
					this.resolve(record);
					this.resetForm("");
					this.dialog = false;
					this.$toast.success(
						`Contact ${this.isEditing ? "updated" : "created"} successfully.`,
						{ timeout: calculateTimeout(`Contact ${this.isEditing ? "updated" : "created"} successfully.`) }
					);
				} catch (e) {
					Sentry.captureException(e, {
						tags: {
							method: "agree",
							file: "CreateCustomerDialog",
						},
					});
					const errorMessage = getMessageFromError(e);
					this.$toast.error(errorMessage, { timeout: calculateTimeout(errorMessage) });
				} finally {
					this.creating = false;
				}
			},
			cancel() {
				this.resetForm("");
				this.dialog = false;
				this.resolve(false);
				this.$emit("createCustomerDialogClosed");
			},
			pasteEvent(event) {
				this.phoneNumber = event.clipboardData?.getData("text")?.replace(/\D/g, "");
			},
			phoneNumberChanged(number, phoneObject) {
				if (this.validPhone) {
					this.$nextTick(() => {
						this.phoneNumber = phoneObject.formatted;
					});
				}
			},
			validatePhoneNumber(args) {
				this.validPhone = args.valid;
			},
			phoneNumberBlur() {
				this.showPhoneNumberFieldErrors = true;
			},
			phoneNumberFocus() {
				this.showPhoneNumberFieldErrors = false;
			}
		},
		computed: {
			buttonText() {
				if (this.isEditing) {
					return this.creating ? "Updating" : "Update";
				}
				return this.creating ? "Creating" : "Create";
			},
			isDirty() {
				if (!this.isEditing) {
					return false;
				}

				const isDirty =
					this.customer.firstname !== this.initialCustomer.firstname ||
					this.customer.lastname !== this.initialCustomer.lastname ||
					this.customer.companyName !== this.initialCustomer.companyName ||
					this.emailAddress !== this.initialCustomer.EmailAddresses[0]?.emailAddress ||
					this.phoneNumber !== this.initialCustomer.PhoneNumbers[0]?.phoneNumber;
				return isDirty;
			},
			firstNameRules() {
				const rules = [];
				rules.push(VuetifyValidation.MaxLengthValidator(25, 'first name').isValid);
				return rules;
			},
			lastNameRules() {
				const rules = [];
				rules.push(VuetifyValidation.MaxLengthValidator(30, 'last name').isValid);
				return rules;
			},

			companyNameRules() {
				const rules = [];
				rules.push(VuetifyValidation.MaxLengthValidator(50, 'company name').isValid);
				return rules;
			},
			emailRules() {
				// TODO: create a common validation file for all the validation rules. Ref: https://github.com/liveswitch/concierge-admin/blob/main/src/components/extensions/Validation.ts
				const rules = [];
				if (this.emailMandatoryText) {
					rules.push((v) => !!v || this.emailMandatoryText);
				}
				rules.push((v) => !v || v == "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || "Email must be valid.");
				rules.push((v) => !v || v == "" || v.length <= 125 || "Please enter a shorter email.");
				return rules;
			},
			createButtonDisabled() {
				return this.creating || (this.isEditing && (!this.isFormValid || !this.validPhone));
			},
		},
	};
</script>
